// File: src/hooks/useNotifications.js

import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useGlobalToast } from "components/GlobalToastProvider";
import {
  getNotifications,
  markNotificationsRead,
  markAllNotificationsRead,
} from "../api/notifications";

export const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentListLength, setCurrentListLength] = useState(5);
  const [loadingMore, setLoadingMore] = useState(false);
  const limit = 5;
  const { showToast } = useGlobalToast();
  const history = useHistory();

  // State for deactivation confirmation dialog
  const [deactivationDialogOpen, setDeactivationDialogOpen] = useState(false);
  const [deactivationDialogData, setDeactivationDialogData] = useState(null);

  const load = async (reset = false, poll = false) => {
    const fetchOffset = poll || reset ? 0 : notifications.length;

    const { data } = await getNotifications(userId, fetchOffset, limit);

    setNotifications((prev) => {
      if (poll) {
        const newIds = new Set(data.map((n) => n.id));
        const rest = prev.filter((n) => !newIds.has(n.id));
        return [...data, ...rest];
      } else if (reset) {
        return data;
      } else {
        return [...prev, ...data];
      }
    });

    if (!poll) {
      setHasMore(data.length === limit);
    }
  };

  useEffect(() => {
    if (!userId) return;
    load(true);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const pollingInterval = setInterval(() => {
      load(false, true);
    }, 5000);
    return () => clearInterval(pollingInterval);
  }, [userId, currentListLength]);

  useEffect(() => {
    setUnreadCount(notifications.filter((n) => !n.is_read).length);
  }, [notifications]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    load(true);
    setCurrentListLength(5);
  };

  const handleMarkAll = async () => {
    await markAllNotificationsRead(userId);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    showToast({ message: "Notifications have been marked as read" });
  };

  const handleClick = async (n) => {
    if (!n.is_read) {
      await markNotificationsRead(userId, [n.id]);
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    }

    const metadata = n.metadata || {};

    // Check if this is a deactivation confirmation notification
    if (
      metadata.action === "confirm_deactivation" ||
      (metadata.client_id && (n.type === "deactivation" || metadata.deactivation_related))
    ) {
      const clientId = metadata.client_id;
      if (clientId) {
        // Open the deactivation confirmation dialog
        setDeactivationDialogData({
          clientId,
          clientName: metadata.client_name || null,
          scheduledDeactivationDate: metadata.scheduled_deactivation_date || null,
          dropReason: metadata.drop_reason || null,
          churnReason: metadata.churn_reason || null,
          departedTo: metadata.departed_to || null,
        });
        setDeactivationDialogOpen(true);
        handleClose(); // Close notification dropdown
        return; // Don't navigate
      }
    }

    // Handle navigation based on notification type
    if (n.type === "clinical") {
      let redirectUrl = null;

      switch (metadata.action) {
        case "client_assigned":
        case "assessment_scheduled":
          redirectUrl = `/clients/${metadata.client_id}/dashboard`;
          break;
        case "tech_check_tablet_recommended":
          if (metadata.client_id) {
            redirectUrl = `/clients/${metadata.client_id}/dashboard`;
          }
          break;
        case "schedule_assessment":
          redirectUrl = `/clinician-schedule/create-event?name=${encodeURIComponent(metadata.client_name || "")}&serviceType=ASSESSMENT`;
          break;
        case "schedule_treatment_plan_review":
          redirectUrl = `/clinician-schedule/create-event?name=${encodeURIComponent(metadata.client_name || "")}&serviceType=ASSESSMENT`;
          break;
        case "auth_submitted":
          redirectUrl = `/clients/${metadata.client_id}/insurance#insurance-authorizations`;
          break;
        case "auth_approved":
          redirectUrl = `/clients/${metadata.client_id}/insurance#insurance-authorizations`;
          break;
        case "upload_treatment_plan":
          redirectUrl = `/clients/${metadata.client_id}/documents?uploadDocType=TREATMENT_PLAN`;
          break;
        case "document_uploaded":
          if (metadata.client_id) {
            redirectUrl = `/clients/${metadata.client_id}/documents`;
          }
          break;
        case "onboarding_packet_completed":
          redirectUrl = `/document-viewer/${n.receiver_id}/${metadata.document_id}`;
          break;
        case "treatment_plan_suggestion_ready":
          redirectUrl = `/document-viewer/${n.receiver_id}/${metadata.document_id}?role=clinician&documentType=primary`;
          break;
        case "sdoh_assessment_completed":
          redirectUrl = `/document-viewer/${n.receiver_id}/${metadata.document_id}?role=clinician&documentType=document`;
          break;
        case "ai_session_note_completed":
          redirectUrl = `/billing?billableTimeId=${n.entity_id}`;
          break;
        case "diagnostic_document_uploaded":
          // For diagnostic documents, redirect to the client's documents page
          if (metadata.client_id) {
            redirectUrl = `/clients/${metadata.client_id}/documents`;
          }
          break;
        case "document_awaiting_supervisor_signature":
        case "document_awaiting_director_signature":
          if (metadata.client_id && metadata.document_id) {
            redirectUrl = `/clients/${metadata.client_id}/documents?verifyDocumentId=${metadata.document_id}`;
          }
          break;
      }

      if (redirectUrl) {
        history.push(redirectUrl);
        handleClose();
      }
    } else if (n.metadata?.redirect_url) {
      // For non-clinical notifications, use redirect_url if provided
      history.push(`/${n.metadata.redirect_url}`);
      handleClose();
    }
  };

  const handleCloseDeactivationDialog = () => {
    setDeactivationDialogOpen(false);
    setDeactivationDialogData(null);
  };

  // Assessment toasts are a projection of the unread notifications above, not a queue anyone
  // pushes to: the toast exists because the row is unread, and goes away when it is read.

  // Bridges the gap between clicking an action and the server confirming it: a poll landing in
  // that window still reports the row as unread, which would flash the toast back on screen.
  const dismissedToastIds = useRef(new Set());

  const forgetToast = (notification) => {
    dismissedToastIds.current.add(notification.id);
    setNotifications((prev) =>
      prev.map((x) => (x.id === notification.id ? { ...x, is_read: true } : x))
    );
  };

  // "View Profile": mark read and navigate, exactly as clicking the row in the dropdown does.
  const handleToastView = (notification) => {
    forgetToast(notification);
    return handleClick(notification);
  };

  // "Dismiss": mark read and nothing else.
  const handleToastDismiss = async (notification) => {
    forgetToast(notification);
    if (!notification.is_read) {
      await markNotificationsRead(userId, [notification.id]);
    }
  };

  const unreadAssessmentNotifications = notifications.filter(
    (n) =>
      !n.is_read &&
      n.metadata?.action === "assessment_scheduled" &&
      !dismissedToastIds.current.has(n.id)
  );

  const handleLoadMore = async () => {
    setLoadingMore(true);
    setCurrentListLength((prev) => prev + 5); // Increase the limit for next fetch
    await load();
    setLoadingMore(false);
  };

  if (!userId) {
    return {
      notifications: [],
      unreadCount: 0,
      anchorEl: null,
      hasMore: false,
      loadingMore: false,
      deactivationDialogOpen: false,
      deactivationDialogData: null,
      unreadAssessmentNotifications: [],
      handlers: {
        handleOpen: () => {},
        handleClose: () => {},
        handleMarkAll: () => {},
        handleClick: () => {},
        handleLoadMore: () => {},
        handleCloseDeactivationDialog: () => {},
        handleToastView: () => {},
        handleToastDismiss: () => {},
      },
    };
  }

  return {
    notifications,
    unreadCount,
    anchorEl,
    hasMore,
    loadingMore,
    deactivationDialogOpen,
    deactivationDialogData,
    unreadAssessmentNotifications,
    handlers: {
      handleOpen,
      handleClose,
      handleMarkAll,
      handleClick,
      handleLoadMore,
      handleCloseDeactivationDialog,
      handleToastView,
      handleToastDismiss,
    },
  };
};
